import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  conditionSearchParam,
  decodeConditions,
  encodeConditions,
} from '@/components/condition-filter'
import { paymentConditionFields } from '@/components/condition-filter/accounting-fields'
import type {
  PaymentDirection,
  PaymentFilters,
  PaymentSort,
  PaymentStatus,
} from '@/features/accounting-payments/api'
import { PaymentList } from '@/features/accounting-payments/payment-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the payments list.
 *
 * Route definition, guard, render — nothing else, per the route-shell rule in
 * `apps/company/AGENTS.md`. `validateSearch` belongs here because the URL is a
 * route concern; what the screen does with the values is the feature's. The
 * filters live in the search params rather than in a store, so a filtered view
 * is linkable and survives a reload.
 *
 * The grant guard is presentation only. The API enforces
 * `accounting_payment.view` on every payment endpoint, so a stale grant still
 * 403s server-side; this only keeps a user without the grant out of a screen
 * they cannot use.
 *
 * `payments.new.tsx` and `payments.$paymentId.tsx` are CHILDREN of this route
 * in flat file routing, so without an Outlet they never mount and their URLs
 * silently render this list instead.
 */
export const Route = createFileRoute('/_authenticated/accounting/payments')({
  validateSearch: (search: Record<string, unknown>): PaymentFilters => ({
    c: conditionSearchParam(search.c),
    match: search.match === 'any' ? 'any' : undefined,
    direction: isPaymentDirection(search.direction) ? search.direction : undefined,
    status: isPaymentStatus(search.status) ? search.status : undefined,
    partner: typeof search.partner === 'string' && search.partner ? search.partner : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    page: positiveInteger(search.page),
    perPage: [10, 25, 50, 100].includes(Number(search.perPage))
      ? Number(search.perPage)
      : undefined,
    sort: isPaymentSort(search.sort) ? search.sort : undefined,
    sortDirection:
      search.sortDirection === 'asc' || search.sortDirection === 'desc'
        ? search.sortDirection
        : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_payment.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingPaymentsRoute,
})

function AccountingPaymentsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const search = Route.useSearch()
  const { conditions, match } = useMemo(
    () => decodeConditions({ c: search.c, match: search.match }, paymentConditionFields),
    [search.c, search.match],
  )
  const params = { ...search, conditions, match }

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <PaymentList
      params={params}
      onParamsChange={(next) =>
        void navigate({
          to: '/accounting/payments',
          search: {
            ...next,
            ...encodeConditions(next.conditions ?? [], next.match ?? 'all'),
            conditions: undefined,
          },
        })
      }
    />
  )
}

const PAYMENT_DIRECTIONS: PaymentDirection[] = ['inbound', 'outbound']
const PAYMENT_STATUSES: PaymentStatus[] = ['draft', 'confirmed', 'cancelled']

function isPaymentDirection(value: unknown): value is PaymentDirection {
  return typeof value === 'string' && (PAYMENT_DIRECTIONS as string[]).includes(value)
}

function isPaymentStatus(value: unknown): value is PaymentStatus {
  return typeof value === 'string' && (PAYMENT_STATUSES as string[]).includes(value)
}

function isPaymentSort(value: unknown): value is PaymentSort {
  return [
    'payment_date',
    'payment_number',
    'direction',
    'partner',
    'method',
    'reference',
    'amount',
    'status',
  ].includes(String(value))
}

function positiveInteger(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}
